import { useState, type FormEvent } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n";
import { formatAuthError } from "../lib/authErrors";
import { trackEvent } from "../api/analytics";
import { checkUsername, setMyUsernameAfterRegistration } from "../api/collab";

export function RegisterScreen({ onLogin }: { onLogin: () => void }) {
  const { register, requestRegisterEmailCode, baseUrl } = useAuth();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Account
  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const uname = username.trim();
    try {
      // Username is optional; if given, validate + reserve availability up front
      // so the user gets immediate feedback before the account is created.
      if (uname) {
        const { valid, available } = await checkUsername(baseUrl, uname);
        if (!valid) { setError(t("register.usernameInvalid")); setBusy(false); return; }
        if (!available) { setError(t("register.usernameTaken")); setBusy(false); return; }
      }
      await register({
        email: email.trim(),
        password,
        emailCode: emailCode.trim(),
        // Play 2a: open signup — no invite code collected ("your first analysis
        // is on us"). The backend still accepts a code if one is ever sent.
        agentMode: "cloud"
      });
      // Set the @username now that we're authenticated (best-effort; it's
      // optional and can be changed later in Settings).
      if (uname) {
        try { await setMyUsernameAfterRegistration(baseUrl, uname); } catch { /* leave it unset; editable in Settings */ }
      }
    } catch (err) {
      const msg = formatAuthError(err, t, t("register.failed"));
      setError(/invitation code/i.test(msg) ? t("register.inviteInvalid") : msg);
    } finally {
      setBusy(false);
    }
  }

  async function sendCode() {
    setCodeBusy(true);
    setCodeSent(false);
    setError(null);
    trackEvent(baseUrl, "signup_started", { method: "register" });
    try {
      await requestRegisterEmailCode(email.trim());
      setCodeSent(true);
    } catch (err) {
      const msg = formatAuthError(err, t, t("register.failed"));
      setError(/invitation code/i.test(msg) ? t("register.inviteInvalid") : msg);
    } finally {
      setCodeBusy(false);
    }
  }

  return (
    <div className="login-wrap wide">
      <section className="auth-card">
      <h1 className="title">{t("register.title")}</h1>
      <p className="subtitle">{t("register.subtitle")}</p>

      {error ? <div className="error-banner">{error}</div> : null}

      <form onSubmit={submit}>
        <section className="panel">
          <h2 className="section-title">{t("register.sectionAccount")}</h2>
          <label className="label" htmlFor="r-email">
            {t("register.email")}
          </label>
          <input
            id="r-email"
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            disabled={busy}
          />
          <button
            type="button"
            className="btn-secondary auth-code-button"
            onClick={() => void sendCode()}
            disabled={busy || codeBusy || !email.trim()}
          >
            {codeBusy ? <span className="spinner inline" /> : t("register.sendCode")}
          </button>
          {codeSent ? <p className="muted-inline auth-code-status">{t("register.codeSent")}</p> : null}
          <label className="label" htmlFor="r-email-code">
            {t("register.emailCode")}
          </label>
          <input
            id="r-email-code"
            className="input"
            value={emailCode}
            onChange={(e) => setEmailCode(e.target.value)}
            autoComplete="one-time-code"
            inputMode="numeric"
            placeholder={t("register.emailCodePlaceholder")}
            required
            disabled={busy}
          />
          <label className="label" htmlFor="r-password">
            {t("register.password")}
          </label>
          <input
            id="r-password"
            className="input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            disabled={busy}
          />
          <label className="label" htmlFor="r-username">
            {t("register.username")}
          </label>
          <input
            id="r-username"
            className="input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={30}
            placeholder={t("register.usernamePlaceholder")}
            disabled={busy}
          />
        </section>

        <button type="submit" className="btn-primary" disabled={busy || !emailCode.trim()}>
          {busy ? <span className="spinner inline" /> : t("register.submit")}
        </button>
      </form>

      <p className="switch-line">
        {t("register.haveAccount")}{" "}
        <button type="button" className="link-btn" onClick={onLogin}>
          {t("register.signIn")}
        </button>
      </p>
      </section>
    </div>
  );
}
